import React, { useState, useEffect } from 'react';

const VEHICLES = [
    { id: 'nexon', name: 'Tata Nexon EV', capacity: 30.2, type: 'car' },
    { id: 'tiago', name: 'Tata Tiago EV', capacity: 19.2, type: 'car' },
    { id: 'mgzs', name: 'MG ZS EV', capacity: 44.5, type: 'car' },
    { id: 'ather', name: 'Ather 450X', capacity: 2.9, type: 'scooter' },
    { id: 'ola', name: 'Ola S1 Pro', capacity: 3.97, type: 'scooter' },
    { id: 'bajaj', name: 'Bajaj Chetak', capacity: 3.2, type: 'scooter' },
    { id: 'tvs', name: 'TVS iQube', capacity: 3.04, type: 'scooter' },
    { id: 'erickshaw', name: 'E-rickshaw (Avg)', capacity: 5.0, type: 'commercial' },
    { id: 'custom', name: 'Custom Battery Size', capacity: 0, type: 'custom' },
];

export default function ChargingCostEstimator() {
    const [selectedVehicle, setSelectedVehicle] = useState(VEHICLES[0].id);
    const [customCapacity, setCustomCapacity] = useState(30.2);
    const [currentCharge, setCurrentCharge] = useState(20);
    const [targetCharge, setTargetCharge] = useState(80);
    const [chargerRate, setChargerRate] = useState(12);
    
    // Auto-adjust target if current becomes greater
    useEffect(() => {
        if (currentCharge >= targetCharge) {
            setTargetCharge(Math.min(100, currentCharge + 10));
        }
    }, [currentCharge, targetCharge]);

    const [results, setResults] = useState({
        unitsNeeded: 0,
        cost: 0,
        timeHrs: 0,
        timeMins: 0,
        savings: 0
    });

    useEffect(() => {
        const vehicle = VEHICLES.find(v => v.id === selectedVehicle);
        const capacity = vehicle.id === 'custom' ? Number(customCapacity) : vehicle.capacity;
        
        const percentToCharge = Math.max(0, targetCharge - currentCharge);
        const unitsNeeded = capacity * (percentToCharge / 100);
        
        const cost = unitsNeeded * Number(chargerRate);
        
        // Guess typical charger speed based on battery size
        const typicalChargerkW = capacity < 10 ? 3.3 : 7.4;
        const totalMinutes = (unitsNeeded / typicalChargerkW) * 60;
        const timeHrs = Math.floor(totalMinutes / 60);
        const timeMins = Math.round(totalMinutes % 60);

        const publicRate = 22; // Typical public fast charger rate
        const savings = unitsNeeded * (publicRate - Number(chargerRate));

        setResults({
            unitsNeeded: Number(unitsNeeded.toFixed(1)),
            cost: Math.round(cost),
            timeHrs,
            timeMins,
            savings: Math.round(savings)
        });
    }, [selectedVehicle, customCapacity, currentCharge, targetCharge, chargerRate]);

    return (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="bg-blue-600 px-6 py-8 text-white text-center">
                <h2 className="text-3xl font-bold mb-2">Charging Cost Estimator</h2>
                <p className="text-blue-100">See how much you save using PeerChargeEV vs Public Stations.</p>
            </div>
            
            <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Inputs */}
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Your Vehicle</label>
                        <select 
                            className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500 p-3 border"
                            value={selectedVehicle}
                            onChange={(e) => setSelectedVehicle(e.target.value)}
                        >
                            {VEHICLES.map(v => (
                                <option key={v.id} value={v.id}>{v.name} {v.id !== 'custom' ? `(${v.capacity} kWh)` : ''}</option>
                            ))}
                        </select>
                        {selectedVehicle === 'custom' && (
                            <div className="mt-3 flex items-center">
                                <input 
                                    type="number" 
                                    className="w-full border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500 p-3 border"
                                    value={customCapacity}
                                    onChange={(e) => setCustomCapacity(Number(e.target.value))}
                                    step="0.1"
                                    min="1"
                                />
                                <span className="ml-3 text-gray-500">kWh</span>
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="flex justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">Current Battery Level</label>
                            <span className="text-sm font-bold text-gray-600">{currentCharge}%</span>
                        </div>
                        <input 
                            type="range" 
                            min="0" 
                            max="90" 
                            value={currentCharge}
                            onChange={(e) => setCurrentCharge(Number(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                    </div>

                    <div>
                        <div className="flex justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">Target Battery Level</label>
                            <span className="text-sm font-bold text-blue-600">{targetCharge}%</span>
                        </div>
                        <input 
                            type="range" 
                            min="10" 
                            max="100" 
                            value={targetCharge}
                            onChange={(e) => setTargetCharge(Math.max(currentCharge + 1, Number(e.target.value)))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Peer Charger Rate (₹/kWh)</label>
                        <div className="relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 sm:text-sm">₹</span>
                            </div>
                            <input 
                                type="number" 
                                className="w-full pl-8 border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500 p-3 border"
                                value={chargerRate}
                                onChange={(e) => setChargerRate(Number(e.target.value))}
                                min="1"
                            />
                        </div>
                    </div>
                </div>

                {/* Outputs */}
                <div className="bg-gray-50 rounded-xl p-6 sm:p-8 flex flex-col justify-center border border-gray-100 relative">
                    {/* Savings Badge */}
                    {results.savings > 0 && (
                        <div className="absolute -top-4 right-4 sm:right-8 bg-green-100 border border-green-200 text-green-800 text-sm font-bold px-4 py-2 rounded-full shadow-sm flex items-center animate-pulse">
                            <span>💚 Save ₹{results.savings} vs public charging</span>
                        </div>
                    )}

                    <div className="text-center mb-8 mt-4">
                        <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">Estimated Charging Cost</p>
                        <p className="text-5xl font-extrabold text-gray-900 mt-2">₹{results.cost}</p>
                    </div>

                    <div className="space-y-4 mb-8">
                        <div className="flex justify-between items-center py-3 border-b border-gray-200">
                            <span className="text-gray-600">Electricity Needed</span>
                            <span className="font-semibold text-gray-900">{results.unitsNeeded} kWh</span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b border-gray-200">
                            <span className="text-gray-600">Est. Time to Charge</span>
                            <span className="font-semibold text-gray-900">
                                {results.timeHrs > 0 ? `${results.timeHrs}h ` : ''}{results.timeMins}m
                            </span>
                        </div>
                        <div className="flex justify-between items-center py-3">
                            <span className="text-gray-600">Public Station Cost (Avg)</span>
                            <span className="font-semibold text-red-500 line-through">₹{results.cost + results.savings}</span>
                        </div>
                    </div>

                    <a href="https://play.google.com/store/apps/details?id=com.niloy.peerchargerev" target="_blank" rel="noopener noreferrer" className="w-full block text-center bg-blue-600 text-white font-bold py-4 px-6 rounded-xl hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-0.5 duration-200">
                        Find a Charger Near You
                    </a>
                </div>
            </div>
        </div>
    );
}
